import { motion } from 'framer-motion';
import { useState } from 'react';
import { LANDING } from '@/constants/testIds';
import TumblerCrop from '@/components/TumblerCrop';
import BottleModal from '@/components/BottleModal';
import { COLLECTIONS } from '@/lib/assets';

const COLLECTION_LIST = [COLLECTIONS.corporate, COLLECTIONS.girls, COLLECTIONS.genz];

const TESTIDS = {
  corporate: LANDING.collectionCorporate,
  girls: LANDING.collectionGirls,
  genz: LANDING.collectionGenz,
};

export default function Collections() {
  const [open, setOpen] = useState(null); // collection id

  return (
    <section id="collections" className="bg-[#FAF8F4] py-32 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-24">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-6"
            >
              Chapter 03 · The Collections
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-[-0.03em] text-[#111]"
            >
              Three moods.
              <br />
              <span className="font-serif italic text-[#111]/70">One ritual.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="max-w-sm text-[15px] leading-relaxed text-[#111]/60"
          >
            Each KIVO drop is engineered identically — only its voice changes.
            Click any bottle to rotate, inspect and choose the one that sounds
            like the day you’re about to have.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {COLLECTION_LIST.map((c, i) => (
            <motion.button
              type="button"
              key={c.id}
              data-testid={TESTIDS[c.id]}
              onClick={() => setOpen(c.id)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover="hover"
              className="group relative overflow-hidden rounded-3xl p-8 lg:p-10 flex flex-col h-[600px] text-left cursor-pointer"
              style={{ backgroundColor: c.bg, color: c.fg }}
            >
              <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase opacity-60">
                <span>0{i + 1}</span>
                <span>{c.name}</span>
              </div>

              <div className="mt-6 font-display text-[clamp(1.75rem,3vw,2.5rem)] leading-[0.95] tracking-[-0.02em]">
                {c.quote.map((q, qi) => (
                  <span key={qi} className="block">
                    {q}
                  </span>
                ))}
              </div>

              <div className="relative flex-1 mt-6 overflow-hidden rounded-xl">
                <motion.div
                  variants={{ hover: { scale: 1.07, rotate: -2 } }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  {c.cardImage ? (
                    <div
                      className="absolute inset-0 bg-no-repeat bg-cover bg-center"
                      style={{ backgroundImage: `url(${c.cardImage})` }}
                    />
                  ) : (
                    <TumblerCrop
                      row={c.sheet.row}
                      col={c.sheet.col}
                      className="absolute inset-0"
                    />
                  )}
                </motion.div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <p className="max-w-[18ch] text-[12px] leading-snug opacity-80">{c.sub}</p>
                <span
                  className="text-[11px] tracking-[0.25em] uppercase flex items-center gap-2 group-hover:gap-3 transition-all"
                  style={{ color: c.accent }}
                >
                  View 360°<span aria-hidden>→</span>
                </span>
              </div><a href={`https://wa.me/919324597297?text=Hi! I want to order KIVO - ${c.name} tumbler`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="w-full text-center text-[11px] tracking-[0.25em] uppercase px-3 py-2 rounded-full border transition-all hover:opacity-80" style={{ borderColor: c.accent, color: c.accent, background: 'transparent' }}>
                  ORDER ON WHATSAPP
                </a>

              {/* hover glow */}
              <div
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                style={{
                  background: `radial-gradient(60% 50% at 50% 100%, ${c.accent}33 0%, transparent 70%)`,
                }}
              />
            </motion.button>
          ))}
        </div>
      </div>

      {open && (
        <BottleModal collection={COLLECTIONS[open]} onClose={() => setOpen(null)} />
      )}
    </section>
  );
}
